import React, { useId } from 'react';
import { cn } from '@/lib/utils';

interface TextFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  required?: boolean;
  optional?: boolean;
  labelAction?: React.ReactNode;
  errors?: string[];
}

export function TextField({
  label,
  required,
  optional,
  labelAction,
  errors,
  className,
  id: providedId,
  ...inputProps
}: TextFieldProps) {
  const generatedId = useId();
  const describedBy = inputProps['aria-describedby'];
  // Callers pass the legacy id (e.g. `user_email`) so existing specs and the
  // company-autocomplete initialiser keep resolving; fall back otherwise.
  const id = providedId ?? generatedId;
  const errorId = `${id}-error`;
  const hasErrors = Boolean(errors?.length);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={id} className="text-sm font-medium text-neutral-1300">
          {label}
          {required ? (
            <span className="text-orange-600" aria-hidden="true">
              {' '}
              *
            </span>
          ) : null}
          {optional ? <span className="ml-1 font-medium text-neutral-800">(Optional)</span> : null}
        </label>
        {labelAction}
      </div>

      <input
        {...inputProps}
        id={id}
        required={required}
        aria-invalid={hasErrors || undefined}
        aria-describedby={[describedBy, hasErrors ? errorId : null].filter(Boolean).join(' ') || undefined}
        className={cn(
          'h-12 w-full rounded-md border bg-neutral-100 px-4 text-neutral-1300 placeholder:text-neutral-600',
          'focus-base',
          hasErrors ? 'border-gui-error' : 'border-neutral-300',
          className,
        )}
      />

      {hasErrors ? (
        <p id={errorId} className="text-xs text-orange-900">
          {errors?.join(', ')}
        </p>
      ) : null}
    </div>
  );
}

export default TextField;
